import type { LeaderboardClient } from "./LeaderboardClient.js";

/** 제출 1건 — 어느 리더보드에 어떤 점수를. */
export interface LeaderboardEntry {
  readonly leaderboardId: string;
  readonly score: number;
}

/**
 * 리더보드 제출기 추상 base — "입력 → 제출할 (leaderboardId, score)"의 결정만 하위 클래스에 위임하고,
 * 가용성 체크 + 유효성(유한수) 가드 + 클라이언트 위임은 공통으로 처리한다. 도메인(챕터/환생 등)별로
 * 상속해 `resolveEntry`만 구현하면 된다(예: ChapterRankingLeaderboard).
 *
 * 플랫폼 분기(GPGS/Game Center)는 주입된 `LeaderboardClient`가 담당하므로 하위 클래스도 플랫폼을 모른다.
 * 순수(Capacitor 무의존) — 단위테스트는 mock client로 100% 커버.
 *
 * @typeParam TInput 점수 산출 입력 타입(예: 챕터=number). 입력이 없으면 void.
 */
export abstract class LeaderboardSubmitter<TInput = void> {
  constructor(protected readonly client: LeaderboardClient) {}

  /** 입력으로부터 제출 엔트리 결정. 제출하지 않을 입력(0/음수 등)이면 null. */
  protected abstract resolveEntry(input: TInput): LeaderboardEntry | null;

  /**
   * 제출 — 가용 + 엔트리 유효(유한 score) 시에만 client로 제출. 제출했으면 true.
   * 실패해도 throw하지 않는다(client가 graceful) — 호출부(이벤트 핸들러)를 막지 않는다.
   */
  async submit(input: TInput): Promise<boolean> {
    if (!this.client.isAvailable()) return false;
    const entry = this.resolveEntry(input);
    if (!entry || !Number.isFinite(entry.score)) return false;
    await this.client.submitScore(entry.leaderboardId, entry.score);
    return true;
  }
}
